import React, { useState, useMemo, useEffect } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { 
  Box, Button, MenuItem, Select, FormControl, InputLabel, 
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Typography, IconButton 
} from '@mui/material';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DateAdapter from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import koLocale from 'date-fns/locale/ko';
import { addMonths, setDate, setMonth, setYear, lastDayOfMonth } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { getNumberComma } from '../../../utils/numberUtils';

const PurchaseBillPlanList = ({ taxList = [], onSave, purchasePlans: dbPlans }) => {
  const { values, setFieldValue } = useFormikContext();
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (dbPlans && dbPlans.length > 0) {
      setFieldValue('purchasePlans', dbPlans);
    }
  }, [dbPlans, setFieldValue]);

  const [formState, setFormState] = useState({
    partnerNm: '',
    partnerUuid: '',
    usrUuid: '',
    amount: '', 
    startMonth: new Date(), // 매입 시작월 초기값
    receiveDay: 1, 
    splitCount: 1,
    invTxt: '' 
  });

  // ... (getPartnerName, addedPartners, filteredTaxList, currentTabData 로직 동일)
  const getPartnerName = (uuid, currentNm) => {
    const partner = taxList?.find(t => t.partnerUuid === uuid);
    return partner ? partner.partnerNm : (currentNm || '미등록 거래처');
  };

  const addedPartners = useMemo(() => {
    const plans = values?.purchasePlans || [];
    const uniqueUuids = [...new Set(plans.map(p => p.partnerUuid))].filter(Boolean);
    return uniqueUuids.map(uuid => {
      const firstMatch = plans.find(p => p.partnerUuid === uuid);
      return { partnerUuid: uuid, partnerNm: getPartnerName(uuid, firstMatch?.partnerNm) };
    });
  }, [values?.purchasePlans, taxList]);

  const filteredTaxList = useMemo(() => {
    if (!taxList) return [];
    const addedUuids = (values?.purchasePlans || []).map(p => p.partnerUuid);
    return taxList.filter(item => !addedUuids.includes(item.partnerUuid));
  }, [taxList, values?.purchasePlans]);

  const currentTabData = useMemo(() => {
    const currentUuid = addedPartners[tabIndex]?.partnerUuid;
    return (values?.purchasePlans || [])
      .map((plan, idx) => ({ ...plan, originalIndex: idx }))
      .filter(plan => plan.partnerUuid === currentUuid);
  }, [values?.purchasePlans, addedPartners, tabIndex]);

  const currentTabTotal = currentTabData.reduce((acc, cur) => acc + Number(cur.amount || 0), 0);
  const originalTotal = useMemo(() => {
    const currentUuid = addedPartners[tabIndex]?.partnerUuid;
    const partner = taxList?.find(t => t.partnerUuid === currentUuid);
    return partner ? partner.billSumTotal : 0;
  }, [addedPartners, tabIndex, taxList]);
  const diffAmount = currentTabTotal - originalTotal;

  const handleAddPlan = (push) => {
    const count = Number(formState.splitCount) || 1;
    const totalAmount = Number(formState.amount);
    const splitAmt = Math.floor(totalAmount / count);
    
    // 선택된 시작월(연/월) + 선택된 매입일(일)로 기준 날짜 생성
    let baseDate = setDate(formState.startMonth, formState.receiveDay);

    for (let i = 0; i < count; i++) {
      const finalAmt = i === count - 1 ? totalAmount - (splitAmt * (count - 1)) : splitAmt;
      const purchaseDate = addMonths(baseDate, i);
      push({
        usrUuid: formState.usrUuid,
        partnerUuid: formState.partnerUuid,
        partnerNm: formState.partnerNm,
        reqCompDt: purchaseDate,
        payPlanDt: lastDayOfMonth(addMonths(purchaseDate, 1)),
        content: `${formState.invTxt || ''} (${i + 1}/${count})`,
        amount: finalAmt,
        payYn: 'N'
      });
    }
    setFormState({ ...formState, partnerNm: '', partnerUuid: '', usrUuid: '', amount: '', splitCount: 1, invTxt: '' });
    setTimeout(() => setTabIndex(addedPartners.length), 0);
  };

  const getPayYnText = (val) => {
    if (val === 'Y') return '완료';
    if (val === 'S') return '확인중';
    return '예정';
  };

  const handleTabChange = (event, newValue) => {
    if (diffAmount !== 0) {
      alert(`금액 차액이 존재합니다. 합계를 맞춘 후 이동해 주세요.`);
      return;
    }
    setTabIndex(newValue);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <FieldArray name="purchasePlans">
        {({ push, remove, insert }) => (
          <>
            {/* 상단 컨트롤 영역 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                <FormControl variant="standard" sx={{ minWidth: 180 }}>
                  <InputLabel>매입처 선택</InputLabel>
                  <Select 
                    value={formState.partnerNm} 
                    onChange={(e) => {
                      const data = taxList.find(item => item.partnerNm === e.target.value);
                      if (data) setFormState({ ...formState, partnerNm: e.target.value, partnerUuid: data.partnerUuid, usrUuid: data.crtUsr, amount: data.billSumTotal, invTxt: data.invTxt });
                    }}
                  >
                    {filteredTaxList.length > 0 ? filteredTaxList.map((item) => (
                      <MenuItem key={item.partnerUuid} value={item.partnerNm}>{item.partnerNm}</MenuItem>
                    )) : <MenuItem disabled value="">추가할 매입처 없음</MenuItem>}
                  </Select>
                </FormControl>
                <TextField label="매입 총 금액" variant="standard" sx={{ width: 140 }} value={getNumberComma(formState.amount)} disabled />
                
                {/* 매입 시작월 선택 추가 */}
                <LocalizationProvider dateAdapter={DateAdapter} locale={koLocale}>
                  <DatePicker
                    views={['year', 'month']}
                    label="매입 시작월"
                    inputFormat="yyyy-MM"
                    value={formState.startMonth}
                    onChange={(date) => setFormState({ ...formState, startMonth: date })}
                    renderInput={(params) => <TextField {...params} variant="standard" sx={{ width: 120 }} />}
                    disabled={!formState.partnerNm}
                  />
                </LocalizationProvider>

                <FormControl variant="standard" sx={{ width: 80 }} disabled={!formState.partnerNm}>
                  <InputLabel>매입일(일)</InputLabel>
                  <Select value={formState.receiveDay} onChange={(e) => setFormState({ ...formState, receiveDay: e.target.value })}>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <MenuItem key={d} value={d}>{d}일</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField label="분할 횟수" variant="standard" type="number" sx={{ width: 60 }} value={formState.splitCount} disabled={!formState.partnerNm} onChange={(e) => setFormState({ ...formState, splitCount: e.target.value })} />
                <Button variant="contained" startIcon={<AddIcon />} disabled={!formState.partnerNm} onClick={() => handleAddPlan(push)}>추가</Button>
              </Box>

              <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={() => diffAmount === 0 ? onSave(values.purchasePlans) : alert('금액 차액을 확인하세요.')} disabled={!values?.purchasePlans?.length}>
                수취 내역 저장
              </Button>
            </Box>

            {/* 리스트 및 테이블 생략 (동일) */}
            {addedPartners.length > 0 && (
              <Box>
                <Tabs value={tabIndex} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  {addedPartners.map((p, idx) => <Tab key={p.partnerUuid || idx} label={p.partnerNm} />)}
                </Tabs>
                <TableContainer sx={{ border: '1px solid #eee' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell align="center" width="50">No</TableCell>
                        <TableCell align="center" width="160">매입일자</TableCell>
                        <TableCell align="center">매입내용</TableCell>
                        <TableCell align="center" width="140">매입금액</TableCell>
                        <TableCell align="center" width="160">지급 예정 일자</TableCell>
                        <TableCell align="center" width="100">지급 여부</TableCell>
                        <TableCell align="center" width="90">작업</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentTabData.map((plan, viewIdx) => {
                        const isLocked = plan.payYn !== 'N';
                        return (
                          <TableRow key={plan.originalIndex} sx={{ bgcolor: isLocked ? '#f2f2f2' : 'inherit' }}>
                            <TableCell align="center">{viewIdx + 1}</TableCell>
                            <TableCell>
                              <LocalizationProvider dateAdapter={DateAdapter} locale={koLocale}>
                                <DatePicker
                                  inputFormat={'yyyy-MM-dd'}
                                  mask={'____-__-__'}
                                  value={plan.reqCompDt ? new Date(plan.reqCompDt) : null}
                                  disabled={isLocked}
                                  onChange={(val) => {
                                      setFieldValue(`purchasePlans.${plan.originalIndex}.reqCompDt`, val);
                                      setFieldValue(`purchasePlans.${plan.originalIndex}.payPlanDt`, lastDayOfMonth(addMonths(val, 1)));
                                  }}
                                  renderInput={(params) => <TextField {...params} variant="standard" fullWidth size="small" />}
                                />
                              </LocalizationProvider>
                            </TableCell>
                            <TableCell>
                              <TextField fullWidth variant="standard" size="small" value={plan.content || ''} disabled={isLocked} onChange={(e) => setFieldValue(`purchasePlans.${plan.originalIndex}.content`, e.target.value)} />
                            </TableCell>
                            <TableCell align="right">
                              <TextField 
                                fullWidth variant="standard" size="small" 
                                value={getNumberComma(plan.amount)} 
                                disabled={isLocked} 
                                onChange={(e) => setFieldValue(`purchasePlans.${plan.originalIndex}.amount`, e.target.value.replace(/[^0-9]/g, ''))}
                                inputProps={{ style: { textAlign: 'right' } }}
                              />
                            </TableCell>
                            <TableCell>
                              <LocalizationProvider dateAdapter={DateAdapter} locale={koLocale}>
                                <DatePicker
                                  inputFormat={'yyyy-MM-dd'}
                                  mask={'____-__-__'}
                                  value={plan.payPlanDt ? new Date(plan.payPlanDt) : null}
                                  disabled={isLocked}
                                  onChange={(val) => setFieldValue(`purchasePlans.${plan.originalIndex}.payPlanDt`, val)}
                                  renderInput={(params) => <TextField {...params} variant="standard" fullWidth size="small" />}
                                />
                              </LocalizationProvider>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">{getPayYnText(plan.payYn)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <IconButton size="small" color="primary" disabled={isLocked} onClick={() => insert(plan.originalIndex + 1, { ...plan, originalIndex: undefined, reqCompDt: addMonths(new Date(plan.reqCompDt), 1), payPlanDt: lastDayOfMonth(addMonths(new Date(plan.reqCompDt), 2)), amount: 0, payYn: 'N' })}>
                                  <AddIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" disabled={isLocked} onClick={() => remove(plan.originalIndex)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                          매입 합계 {originalTotal > 0 ? `(원본: ${getNumberComma(originalTotal)})` : '(원본 정보 없음)'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{getNumberComma(currentTabTotal)} 원</TableCell>
                        <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold' }}>
                          {diffAmount !== 0 && (
                            <Typography variant="body2" sx={{ color: diffAmount > 0 ? 'red' : 'blue', fontWeight: 'bold' }}>
                              변동: {diffAmount > 0 ? '+' : ''}{getNumberComma(diffAmount)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </FieldArray>
    </Box>
  );
};

export default PurchaseBillPlanList;
